/**
 * @file home page
 * @author Yangholmes 2024-03-20
 */

import { useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { OpenType } from '@/constants';
import styles from './index.module.scss';
import { IUserLoginInfo } from '@/api/user';
import { IState, namespace, StoreContext } from '@/context';
import { GetServerSidePropsContext } from 'next';
import { CommonHead, Toast } from '@/components';
import ContentCard from '@/components/ContentCard';
import { getNavsList, ITopLevelNav } from '@/api/nav';
import useTabSearchHook from './hooks/useTabSearchHook';
import TabSearchComponent from '@/components/TabSearch';
import getMyCircle, { ICircle } from '@/api/ channel/myCircle';
import getThreadList, { IThreadLisItem } from '@/api/thread/list';
import Carousel, { AttachType, CarouselItem } from '@/components/Carousel';
import { BannerConfigType, getChannelDetail, getHotThreads, getSubChannelTree, IHotThread, ISubChannelTreeRes, TopLevelChannels } from '@/api/home';
import Logger from '@/utils/logger';
import { getResourceUsage } from '@/utils/resourceUsage';
import useBodyContainer from '@/hooks/useBodyContainer';

interface Error {
  code: string | number;
  message: string;
};
export interface HomeProps {
  ua: string;
  error: Error[];
  saltkey: string;
  circleList: ICircle[];
  hotList: IHotThread[];
  navList: ITopLevelNav[];
  account?: IUserLoginInfo;
  sources: ISubChannelTreeRes;
  bannerList: BannerConfigType[];
  defaultChannelActiveSlug: string;
  defaultChannelActiveSort: string;
  channelList: ISubChannelTreeRes[];
  firstPageArticalList: IState[namespace.home]['articalList'][string]['data'];
}


const Home = (props: HomeProps) => {
  const container = useBodyContainer();

  const { channelList, firstPageArticalList, sources, error } = props;

  const { state } = useContext(StoreContext);
  const { track, TRACK_EVENTS } = state[namespace.app].sensors || {};
  
  // 处理 TabSearch组件 数据
  const {tabs, moreTabs, tabSortFilterMap} = useTabSearchHook(channelList);

  useEffect(() => {
    if (error.length) {
      Toast.show({
        content: error[0].message
      });
    }
  }, [error]);

  // PC banner 数据
  const bannerList: CarouselItem[] = (sources?.channel_config?.banner_config || []).map(item => {
    const { link_url, pc_banner_url, video_play_info, open_type = OpenType.CurrentWindow } = item;
    const hasVideo = video_play_info?.video_uid;
  
    return {
      alt: '',
      link: {
        openType: open_type,
        src: link_url,
      },
      type: hasVideo ? AttachType.Video : AttachType.Image,
      src: hasVideo ? video_play_info.definitions[1].src : pc_banner_url,
      ...(hasVideo && { fallbackSrc: pc_banner_url }),
    };
  });

  const onBannerTrackCb = (order: number, link: string) => {
    track?.(TRACK_EVENTS?.[namespace.home].materialClick.name, {
      list_name: '首页',
      banner_order: order,
      banner_url: link,
      position: 'Top',
    }, namespace.home);
  }

  return (
    <div
      className={styles.homeMainContent}
      style={{ marginTop: !bannerList.length ? "0" : "16px" }}
    >
      <CommonHead
        title="DJI大疆社区 - 大疆用户官方交流平台"
        canonicalLink="https://bbs.dji.com/"
        keywords="大疆社区,无人机玩家交流,手持设备玩家交流,无人机玩法,无人机教程,DJI,DJI Mavic,DJI Mini,DJI Air,Osmo Action,Osmo Pocket,DJI Avata,DJI Neo,DJI Flip"
        description="DJI大疆社区是深圳大疆创新打造的用于用户之间技术交流、生活分享和航拍作品、航拍无人机教程讨论的平台。大疆社区立志于汇集民智的力量,让用户和我们一起创造完美的无人机飞行影像产品与卓越的用户体验。"
      />
      {container &&
        ReactDOM.createPortal(
          'DJI大疆社区 - 大疆用户官方交流平台',
          container
        )}
      {/* 轮播组件 */}
      {!!bannerList.length && (
        <div className={styles.bannerContent}>
          <Carousel
            items={bannerList}
            width={640}
            height={120}
            onBannerTrackCb={onBannerTrackCb}
          />
        </div>
      )}
      {/* tab 内容搜索组件 */}
      <div className={styles.mainContent}>
        <TabSearchComponent<IThreadLisItem>
          channel={TopLevelChannels.home_page_recommended_channel}
          id="tid"
          tabs={tabs}
          pageSize={10}
          moreTabs={moreTabs}
          Component={ContentCard} // 内容渲染组件
          tabSortFilterMap={tabSortFilterMap}
          defaultContent={firstPageArticalList} // 默认展示首屏内容
          fetchApi={({ tabKey, sortKey, offset = 10, pageSize }) =>
            getThreadList({
              // 获取数据api
              offset,
              limit: pageSize,
              sort_type: sortKey,
              channel_slug: tabKey,
            })
          }
        />
      </div>
    </div>
  );
};

  const handlePromiseResult = (result:  PromiseSettledResult<any>, setValue: ((value: any) => void), errorList: Error[]) => {
    if (result?.status === 'fulfilled') {
      setValue(result.value || []);
    } else {

      errorList.push(result.reason);
    }
  };

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  // 记录 500 错误
  if (ctx.res?.statusCode === 500) {
    Logger.error({
      type: 'SSR_PC_HOME_PAGE_500_ERROR',
      path: ctx.resolvedUrl,
      message: JSON.stringify({
        ...getResourceUsage(),
        headers: ctx.req?.headers,
        statusCode: 500,
      }),
      user_agent: ctx.req?.headers?.['user-agent'],
      referer: ctx.req?.headers?.['referer'],
    });
  }

  const startTime = Date.now();

  const ua = ctx.req.headers['user-agent'] || '';

  let error: Error[] = [];
  let hotList: IHotThread[] = [];
  let circleList: ICircle[] = [];
  let navList: ITopLevelNav[] = [];
  let bannerList: BannerConfigType[] = [];
  let channelList: ISubChannelTreeRes[] = [];
  let firstPageArticalList: IThreadLisItem[] = [];
  let defaultValues = {
    defaultChannelActiveSort: '',
    defaultChannelActiveSlug: '',
  };
  let sources: ISubChannelTreeRes | [] = {} as unknown as ISubChannelTreeRes;

  const headers = ctx.req ? ctx.req.headers : {};

  try {
    channelList =
      (
        await getSubChannelTree(
          {
            slug: TopLevelChannels.home_page_recommended_channel,
            level_num: 2,
          },
          true,
          headers,
        )
      )?.sub_channel_info_dtos || [];


    defaultValues = {
      defaultChannelActiveSort: channelList[0].order_rule[0],
      defaultChannelActiveSlug: channelList[0].slug,
    };

    const [artical, circle, navs, cmsSources, hots ] = await Promise.allSettled([
      getThreadList(
        {
          channel_slug: defaultValues?.defaultChannelActiveSlug,
          sort_type: defaultValues.defaultChannelActiveSort,
        },
        true,
        headers,
      ),
      getMyCircle(true, headers),
      getNavsList(true),
      getChannelDetail(
        {
          slug: TopLevelChannels.home_page_recommended_channel,
        },
        true,
        headers,
      ),
      getHotThreads(true),
    ]);

    handlePromiseResult(navs, (value: typeof navList) => (navList = value), error);
    handlePromiseResult(hots, (value: typeof hotList) => (hotList = value), error);
    handlePromiseResult(cmsSources, (value: typeof sources) => (sources = value), error);
    handlePromiseResult(circle, (value: typeof circleList) => (circleList = value), error);
    handlePromiseResult(artical, (value: typeof firstPageArticalList) => (firstPageArticalList = value), error);

    Logger.info({
      type: 'SSR_PC_HOME_PAGE',
      path: ctx.resolvedUrl,
      message: JSON.stringify({
        renderTime: Date.now() - startTime,
      }),
    });

  } catch (e) {
    // 捕获首页错误
    Logger.error({
      type: 'SSR_PC_HOME_PAGE_FATAL_ERROR',
      path: ctx.resolvedUrl,
      message: JSON.stringify({
        ...getResourceUsage(),
        headers: ctx.req?.headers,
        errorCtx: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : error,
      }),
      user_agent: ctx.req?.headers?.['user-agent'],
      referer: ctx.req?.headers?.['referer'],
    });
  }

  const matchCookies = ctx.req.headers.cookie?.match(/H5VF_2132_saltkey=(\w+)/);

  return {
    props: {
      ua,
      error,
      navList,
      hotList,
      sources,
      circleList,
      bannerList,
      channelList,
      firstPageArticalList,
      saltkey: matchCookies && matchCookies.length >= 2 ? matchCookies[1] : '',
      defaultChannelActiveSlug: defaultValues?.defaultChannelActiveSlug || null,
      defaultChannelActiveSort: defaultValues?.defaultChannelActiveSort || null,
    },
  };
}

export default Home;
